// @ts-check
import { Component } from '@theme/component';

class FaqNavComponent extends Component {
  requiredRefs = ['scroll'];

  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {HTMLElement[]} */
  #categories = [];
  /** @type {Map<string, HTMLAnchorElement>} */
  #linksById = new Map();
  /** @type {string | null} */
  #activeCategoryId = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    this.#refresh();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.#disconnectObserver();
    this.#categories = [];
    this.#linksById.clear();
    this.#activeCategoryId = null;
  }

  get scrollElement() {
    return /** @type {HTMLElement} */ (this.refs.scroll);
  }

  #refresh() {
    const scope = this.#getCategoryScope();
    const categories = scope ? this.#getCategories(scope) : [];
    const previousActiveCategoryId = this.#activeCategoryId;

    this.#categories = categories;
    this.#renderLinks(categories);
    this.hidden = categories.length === 0;

    if (categories.length === 0) {
      this.#disconnectObserver();
      return;
    }

    this.#setupObserver(categories);

    if (previousActiveCategoryId && this.#linksById.has(previousActiveCategoryId)) {
      this.#setActiveLink(previousActiveCategoryId);
      return;
    }

    this.#setActiveLink(categories[0]?.id ?? null);
  }

  /**
   * @returns {Element | Document}
   */
  #getCategoryScope() {
    const selector = this.dataset.faqNavScopeSelector?.trim();

    if (selector) {
      try {
        const scoped = this.closest(selector);
        if (scoped instanceof Element) return scoped;
      } catch (error) {
        console.warn('Invalid faq nav scope selector', error);
      }
    }

    const customSection = this.closest('.custom-section');
    if (customSection instanceof Element) return customSection;

    return document;
  }

  /**
   * @param {Element | Document} scope
   * @returns {HTMLElement[]}
   */
  #getCategories(scope) {
    return /** @type {HTMLElement[]} */ (
      Array.from(scope.querySelectorAll('[data-faq-category]')).filter((node) => {
        return node instanceof HTMLElement && node.id !== '' && (node.dataset.faqTitle?.trim() ?? '') !== '';
      })
    );
  }

  /**
   * @param {HTMLElement[]} categories
   */
  #renderLinks(categories) {
    this.#linksById.clear();

    const fragment = document.createDocumentFragment();

    categories.forEach((category) => {
      const title = category.dataset.faqTitle?.trim();
      if (!title) return;

      const link = document.createElement('a');
      link.href = `#${category.id}`;
      link.className = 'faq-nav__link';
      link.textContent = title;
      fragment.appendChild(link);
      this.#linksById.set(category.id, link);
    });

    this.scrollElement.replaceChildren(fragment);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('.faq-nav__link');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (!this.contains(link)) return;

    const href = link.getAttribute('href') ?? '';
    if (!href.startsWith('#')) return;

    const categoryId = href.slice(1);
    const category = this.#categories.find((item) => item.id === categoryId) ?? document.getElementById(categoryId);

    if (!(category instanceof HTMLElement)) return;

    event.preventDefault();
    this.#setActiveLink(categoryId, { scrollIntoView: true, smooth: true });
    this.#scrollToCategory(category);
  };

  /**
   * @param {HTMLElement} category
   */
  #scrollToCategory(category) {
    const offset = this.#getScrollOffset();
    const top = Math.max(0, Math.round(window.scrollY + category.getBoundingClientRect().top - offset));

    window.scrollTo({
      top,
      behavior: 'smooth',
    });
  }

  #getScrollOffset() {
    const attrValue = parseInt(this.dataset.faqNavScrollOffset ?? '', 10);
    if (Number.isFinite(attrValue) && attrValue >= 0) {
      return attrValue;
    }

    const headerInner = document.querySelector('floating-header .floating-header__inner');
    if (headerInner instanceof HTMLElement) {
      const height = Math.round(headerInner.getBoundingClientRect().height);
      if (height > 0) return height;
    }

    return 100;
  }

  /**
   * @param {HTMLElement[]} categories
   */
  #setupObserver(categories) {
    this.#disconnectObserver();
    if (typeof IntersectionObserver === 'undefined') return;

    let rootMargin = this.dataset.faqNavRootMargin || '-100px 0px -50% 0px';

    const createObserver = () => {
      this.#observer = new IntersectionObserver(
        (entries) => {
          const visibleEntries = entries.filter((entry) => entry.isIntersecting);
          if (visibleEntries.length === 0) return;

          visibleEntries.sort((a, b) => {
            return Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top);
          });

          const target = visibleEntries[0]?.target;
          if (!(target instanceof HTMLElement)) return;

          this.#setActiveLink(target.id, { scrollIntoView: true });
        },
        { root: null, threshold: 0, rootMargin }
      );
    };

    try {
      createObserver();
    } catch (error) {
      console.warn('Invalid faq nav root margin, using default value', error);
      rootMargin = '-100px 0px -50% 0px';
      createObserver();
    }

    categories.forEach((category) => {
      this.#observer?.observe(category);
    });
  }

  #disconnectObserver() {
    if (!this.#observer) return;
    this.#observer.disconnect();
    this.#observer = null;
  }

  /**
   * @param {string | null} categoryId
   * @param {{ scrollIntoView?: boolean, smooth?: boolean }} [options]
   */
  #setActiveLink(categoryId, options = {}) {
    if (!categoryId) return;
    if (this.#activeCategoryId === categoryId) return;

    const activeClass = this.dataset.faqNavActiveClass || 'faq-nav__link--active';
    const activeLink = this.#linksById.get(categoryId);
    if (!(activeLink instanceof HTMLAnchorElement)) return;

    this.#linksById.forEach((link, id) => {
      const isActive = id === categoryId;
      link.classList.toggle(activeClass, isActive);

      if (isActive) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    this.#activeCategoryId = categoryId;

    if (options.scrollIntoView) {
      const scrollContainer = activeLink.closest('.faq-nav__nav');
      if (scrollContainer instanceof HTMLElement) {
        const linkLeft = activeLink.offsetLeft;
        const linkWidth = activeLink.offsetWidth;
        const containerWidth = scrollContainer.offsetWidth;
        scrollContainer.scrollTo({
          left: linkLeft - (containerWidth - linkWidth) / 2,
          behavior: options.smooth ? 'smooth' : 'auto',
        });
      }
    }
  }
}

if (!customElements.get('faq-nav-component')) {
  customElements.define('faq-nav-component', FaqNavComponent);
}
